import { IconButton, makeStyles, TableCell, TableRow, Tooltip } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/CheckCircle";
import HelpIcon from "@material-ui/icons/Help";
import SendIcon from "@material-ui/icons/Send";
import { format } from "date-fns";
import { enNZ } from "date-fns/locale";
import { useState } from "react";
import { AdviceDelivery, AdviceStatuses, AmlStatuses, ClientStatuses, SearchClientDto } from "../../../api/apiClients";
import { getClientDisplayName } from "../../../common/helpers";
import { useAppDispatch, useAppSelector } from "../../../store";
import { archiveUnarchiveClientAsync, getClientAsync, resendEmailInvitationAsync, resendSmsInvitationAsync } from "../../../store/adviser";
import { Colors, DialogBox, ProductIcon, SubtitleText } from "../../controls/helpers";
import ConfirmSend from "../ConfirmSend";
import { applicationNeedsConfirmation, getAppliedProducts, getAssignedProducts, getJointClientApplications } from "../Helpers";
import { ArchivesIcon, SourceIcon } from "../IconsAndChips";
import Refer from "../Refer";
import { ApplicationAmlStatus, ConfirmationStatus, JointStatus, SatStatus } from "../StatusChips";

enum Mode {
  None,
  Archive,
  Refer,
  Send,
}

const ClientRow = ({ client, isSupportRole, refreshGrid }: { client: SearchClientDto; isSupportRole: boolean; refreshGrid: () => void }) => {
  const { isSubmitting } = useAppSelector((state) => state.adviser);
  const assignedProducts = getAssignedProducts({ ...client });
  const appliedProducts = getAppliedProducts({ ...client });
  const jointAmlStatus = getJointAmlStatus(client);
  const jointClientStatus = getJointClientStatus(client);
  const jointClientSecondaryNames = getJointClientNames(client);
  const [mode, setMode] = useState<Mode>(Mode.None);
  const dispatch = useAppDispatch();
  const classes = useStyles();

  let sentDate = "";
  if (client.emailSentDate) sentDate = format(new Date(client.emailSentDate), "dd MMM yyyy hh:mm a", { locale: enNZ });
  else if (client.smsSentDate) sentDate = format(new Date(client.smsSentDate), "dd MMM yyyy hh:mm a", { locale: enNZ });
  else if (!client.onboardDelivery && client.created) sentDate = format(new Date(client.created), "dd MMM yyyy hh:mm a", { locale: enNZ });

  const openClient = (client: SearchClientDto) => {
    if (client.clientStatus === ClientStatuses.Draft) dispatch(getClientAsync(client.clientId!));
    else {
      if (client.adviceDelivery === AdviceDelivery.InPerson && client.clientStatus === ClientStatuses.None) {
        if (client.adviceStatus !== AdviceStatuses.Completed && !isSupportRole) window.open(`/adviser/sat/${client.clientId}`, "_blank");
        else if (!client.smsSentDate && !client.emailSentDate && !isSupportRole) window.open(`/adviser/sat-application/${client.clientId}`, "_blank");
      } else {
        window.open(`/adviser/${client.clientId}/${!isSupportRole && applicationNeedsConfirmation(client) ? "advice-confirmation" : "client-application"}`, "_blank");
      }
    }
  };

  const archive = async (isArchived: boolean, clientId: string) => {
    await dispatch(archiveUnarchiveClientAsync({ clientId: clientId, archiveClient: isArchived }));
    refreshGrid();
    setMode(Mode.None);
  };

  const sendApplication = async () => {
    if (client?.sendEmail) await dispatch(resendEmailInvitationAsync({ clientId: client.clientId }));
    if (client?.sendSMS) await dispatch(resendSmsInvitationAsync({ clientId: client.clientId }));
    setMode(Mode.Refer);
  };

  const refer = () => {
    refreshGrid();
    setMode(Mode.None);
  };

  return (
    <TableRow className={classes.hoverableRow} onClick={(_) => openClient(client)}>
      <TableCell />
      <TableCell style={{ paddingTop: 0, paddingRight: 0, paddingBottom: 0 }}>
        <SourceIcon adviceDelivery={client.adviceDelivery} referralCode={client.referralCode} onboardDelivery={client.onboardDelivery} />
      </TableCell>
      <TableCell>
        <b>{getClientDisplayName(client)}</b>
      </TableCell>
      <TableCell>{client.adviserName}</TableCell>
      <TableCell>{!!client.created && format(new Date(client.created), "dd MMM yyyy", { locale: enNZ })}</TableCell>
      <TableCell align="center" style={{ paddingTop: 0, paddingBottom: 0 }}>
        {sentDate && (
          <Tooltip title={sentDate}>
            <CheckIcon style={{ color: Colors.Green, marginTop: 5 }} />
          </Tooltip>
        )}
      </TableCell>
      <TableCell style={{ paddingTop: 3, paddingBottom: 3, paddingRight: 0 }} valign="middle">
        {assignedProducts.map((product, i) => (
          <ProductIcon key={i} product={product} highlighted={client.clientStatus === ClientStatuses.Completed && appliedProducts.some((p) => p === product)} />
        ))}
      </TableCell>
      <TableCell valign="middle" style={{ paddingTop: 3, paddingBottom: 3, paddingRight: 0 }}>
        <SatStatus createdDate={client.created} adviceInProgressDate={client.adviceInProgressDate} adviceCompletedDate={client.adviceCompletedDate} adviceStatus={client.adviceStatus!} title="SAT" />
        <ApplicationAmlStatus
          adviceStatus={client.adviceStatus!}
          clientStatus={client.clientStatus}
          amlStatus={client.amlStatus!}
          amlFailedDate={client.amlFailedDate}
          applicationCompletedDate={client.applicationCompletedDate}
          clientInProgressDate={client.clientInProgressDate}
          adviceCompletedDate={client.adviceCompletedDate}
          isRetention={client.kiwiSaverApplication?.isRetention}
          title="Appl/AML"
        />
        <ConfirmationStatus client={client} title="Confirm" />
        <JointStatus
          jointStatusSummary={jointAmlStatus}
          clientStatus={jointClientStatus!}
          clientAmlStatus={client.amlStatus!}
          jointInitialEmailDate={getJointInitialEmailSentDate(client)}
          tooltipSecondaryText={jointClientSecondaryNames!}
          title="Joint"
        />
      </TableCell>
      <TableCell valign="middle" align="right" style={{ paddingTop: 3, paddingBottom: 3, paddingLeft: 3 }}>
        {!isSupportRole && client.clientStatus === ClientStatuses.Draft && (
          <>
            <IconButton
              size="small"
              className={classes.hoverableIconButton}
              onClick={(e) => {
                e.stopPropagation();
                setMode(Mode.Send);
              }}
            >
              <Tooltip title="Send to the client now">
                <SendIcon style={{ color: Colors.Blue }} />
              </Tooltip>
            </IconButton>
          </>
        )}
        {!isSupportRole && (
          <IconButton
            size="small"
            className={classes.hoverableIconButton}
            onClick={(e) => {
              e.stopPropagation();
              setMode(Mode.Archive);
            }}
          >
            <ArchivesIcon isArchive={!client.isArchived!} />
          </IconButton>
        )}
      </TableCell>
      <TableCell />
      <DialogBox
        open={mode === Mode.Archive}
        isSubmitting={isSubmitting}
        icon={<HelpIcon htmlColor={Colors.Green} />}
        ok={() => archive(!client.isArchived, client.clientId!)}
        cancel={() => setMode(Mode.None)}
      >
        <SubtitleText black hideMarginBottom>
          Are you sure you want to {client.isArchived && "un"}archive this application?
        </SubtitleText>
      </DialogBox>
      <ConfirmSend
        cancel={() => setMode(Mode.None)}
        ok={() => sendApplication()}
        onlyKiwiSaver={!!client.kiwiSaverApplication && !client.wealthBuilderApplications?.length && !client.incomeGeneratorApplications?.length}
        open={mode === Mode.Send}
      />
      <Refer ok={refer} cancel={refer} open={mode === Mode.Refer} email={client.email!} firstName={client.firstName!} lastName={client.lastName || ""} mobile={client.mobile || ""} />
    </TableRow>
  );
};

const getJointAmlStatus = (client: SearchClientDto): AmlStatuses | undefined => {
  const jointAccounts = getJointClientApplications(client);
  if (!jointAccounts.length) return undefined;
  else if (jointAccounts.every((p) => p.amlStatus?.split(", ").includes(AmlStatuses.Successful))) return AmlStatuses.Successful;
  else if (jointAccounts.some((p) => p.amlStatus?.split(", ").includes(AmlStatuses.Failed))) return AmlStatuses.Failed;
  else if (jointAccounts.some((p) => p)) return AmlStatuses.None;
  return undefined;
};

const getJointInitialEmailSentDate = (client: SearchClientDto): Date | undefined => {
  const jointAccounts = getJointClientApplications(client);
  const result = jointAccounts.filter((p) => p.invitationSentDate != undefined);
  if (result != undefined && result.length > 0) return result[0].invitationSentDate;
  return undefined;
};

const getJointClientStatus = (client: SearchClientDto): ClientStatuses | undefined => {
  const jointAccounts = getJointClientApplications(client);
  if (!jointAccounts.length) return undefined;
  else if (jointAccounts.every((p) => p.clientStatus == ClientStatuses.Completed)) return ClientStatuses.Completed;
  else if (jointAccounts.some((p) => p.clientStatus == ClientStatuses.InProgress)) return ClientStatuses.InProgress;
  return undefined;
};

const getJointClientNames = (client: SearchClientDto): string | undefined => {
  const jointAccounts = getJointClientApplications(client);
  if (!jointAccounts.length) return undefined;

  let jointSecondaryNames = "";
  const secondaryNames = [] as string[];

  jointAccounts.forEach((account) => {
    secondaryNames.push(account.firstName! + " " + account.lastName!);
  });

  jointSecondaryNames = secondaryNames.join(", ");
  return "\n" + jointSecondaryNames;
};

const useStyles = makeStyles(
  {
    hoverableRow: {
      cursor: "pointer",
      "&:hover": {
        backgroundColor: `${Colors.MediumGrey} !important`,
      },
      "&:nth-child(even)": {
        backgroundColor: Colors.White,
      },
      "& td:first-child": {
        backgroundColor: `${Colors.LightGrey} !important`,
      },
      "& td:last-child": {
        backgroundColor: `${Colors.LightGrey} !important`,
      },
    },
    hoverableIconButton: {
      visibility: "hidden",
      "$hoverableRow:hover &": {
        visibility: "visible",
      },
    },
  },
  { name: "ClientRow" }
);

export default ClientRow;
